import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';
import {
  buildBossImageLookup,
  buildNikkeCatalogLookupFromItems,
  normalizeNikkeCatalogRow,
} from '../domain/nikkeCatalog.js';

const DEFAULT_NIKKE_DATA_API_URL = 'https://dororaid-data.kukla1255.workers.dev';
const NIKKE_DATA_API_URL = String(
  import.meta.env?.VITE_DORORAID_DATA_API_URL || DEFAULT_NIKKE_DATA_API_URL,
).trim().replace(/\/+$/, '');
const D1_PAGE_LIMIT = 200;
const D1_MAX_ROWS = 2000;
const D1_TIMEOUT_MS = 3500;
const CATALOG_CACHE_TTL_MS = 5 * 60_000;

const nikkeCatalogCache = new Map();
const nikkeCatalogPromises = new Map();
let bossImageCatalogCache = null;
let bossImageCatalogPromise = null;

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function errorMessage(error) {
  return error instanceof Error ? error.message : String(error || '알 수 없는 오류');
}

function cacheFresh(entry) {
  return Boolean(entry && Date.now() - Number(entry.cachedAt || 0) < CATALOG_CACHE_TTL_MS);
}

function nikkeCatalogCacheKey(options = {}) {
  return options.includeInactive ? 'all' : 'active';
}

async function loadNikkeCatalogFromD1({ includeInactive = false, forceRefresh = false } = {}) {
  if (!NIKKE_DATA_API_URL) throw new Error('D1 카탈로그 API 주소가 설정되지 않았습니다.');

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), D1_TIMEOUT_MS);
  const rows = [];
  const cacheBust = forceRefresh ? String(Date.now()) : '';
  let offset = 0;

  try {
    while (rows.length < D1_MAX_ROWS) {
      const url = new URL(`${NIKKE_DATA_API_URL}/v1/nikke/catalog`);
      url.searchParams.set('limit', String(D1_PAGE_LIMIT));
      url.searchParams.set('offset', String(offset));
      if (includeInactive) url.searchParams.set('include_inactive', 'true');
      if (cacheBust) url.searchParams.set('_refresh', cacheBust);

      const response = await fetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
        cache: forceRefresh ? 'no-store' : 'default',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`D1 카탈로그 응답 오류 (${response.status})`);

      const payload = await response.json();
      if (!Array.isArray(payload?.items)) throw new Error('D1 카탈로그 응답 형식이 올바르지 않습니다.');
      rows.push(...payload.items);

      const nextOffset = payload?.page?.nextOffset;
      if (nextOffset === null || nextOffset === undefined) break;
      const parsedNextOffset = Number(nextOffset);
      if (!Number.isInteger(parsedNextOffset) || parsedNextOffset <= offset) {
        throw new Error('D1 카탈로그 페이지 정보가 올바르지 않습니다.');
      }
      offset = parsedNextOffset;
    }
  } catch (error) {
    if (error?.name === 'AbortError') throw new Error('D1 카탈로그 응답 시간이 초과되었습니다.');
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!rows.length) throw new Error('D1 니케 카탈로그가 비어 있습니다.');
  const normalizedRows = rows.slice(0, D1_MAX_ROWS).map(normalizeNikkeCatalogRow);
  return {
    rows: normalizedRows,
    lookup: buildNikkeCatalogLookupFromItems(normalizedRows),
    source: 'cloudflare-d1',
    warnings: [],
  };
}

async function loadNikkeCatalogFromSupabase({ includeInactive = false } = {}) {
  let query = requireClient()
    .from('nikke_catalog')
    .select('*')
    .order('display_order', { ascending: true })
    .order('resource_id', { ascending: true });
  if (!includeInactive) query = query.eq('active', true);
  const { data, error } = await query.limit(D1_MAX_ROWS);
  if (error) throw new Error(`Supabase 니케 DB를 불러오지 못했습니다: ${error.message}`);
  const rawRows = data || [];
  const rows = rawRows.map(normalizeNikkeCatalogRow);
  return {
    rows,
    lookup: buildNikkeCatalogLookupFromItems(rows),
    source: 'supabase',
    warnings: [],
  };
}

async function fetchNikkeCatalog(options = {}) {
  let d1Error;
  try {
    return await loadNikkeCatalogFromD1(options);
  } catch (error) {
    d1Error = error;
  }

  try {
    const fallback = await loadNikkeCatalogFromSupabase(options);
    return {
      ...fallback,
      warnings: [`D1 카탈로그 조회 실패로 Supabase를 사용했습니다: ${errorMessage(d1Error)}`],
    };
  } catch (supabaseError) {
    throw new Error(
      `니케 DB를 불러오지 못했습니다. D1: ${errorMessage(d1Error)} / Supabase: ${errorMessage(supabaseError)}`,
    );
  }
}

export async function loadNikkeCatalog(options = {}) {
  const key = nikkeCatalogCacheKey(options);
  const forceRefresh = Boolean(options.forceRefresh);
  const cached = nikkeCatalogCache.get(key);
  if (!forceRefresh && cacheFresh(cached)) return cached.value;

  const existingPromise = nikkeCatalogPromises.get(key);
  if (!forceRefresh && existingPromise) return existingPromise;

  const requestOptions = {
    includeInactive: Boolean(options.includeInactive),
    forceRefresh,
  };
  const promise = fetchNikkeCatalog(requestOptions)
    .then((value) => {
      nikkeCatalogCache.set(key, { value, cachedAt: Date.now() });
      return value;
    })
    .finally(() => {
      if (nikkeCatalogPromises.get(key) === promise) nikkeCatalogPromises.delete(key);
    });
  nikkeCatalogPromises.set(key, promise);
  return promise;
}

export async function loadElementIcons() {
  const { data, error } = await requireClient()
    .from('element_icons')
    .select('element, element_code, icon_key, image_url, image_data')
    .order('element', { ascending: true });
  if (error) throw new Error(`속성 아이콘을 불러오지 못했습니다: ${error.message}`);
  return data || [];
}

function normalizeBossMasterImageRows(rows = []) {
  return rows.map((row) => ({
    id: `boss-master:${String(row.icon_id || '').trim()}`,
    boss_name: String(row.canonical_name || row.boss_name || '').trim(),
    boss_key: String(row.boss_key || '').trim(),
    boss_image_url: String(row.boss_image_url || '').trim(),
    category: 'boss_master',
    icon_id: String(row.icon_id || '').trim(),
    monster_model_id: String(row.source_monster_model_id || row.monster_model_id || '').trim(),
    image_status: String(row.image_status || '').trim(),
  }));
}

async function fetchBossImageCatalog() {
  const client = requireClient();
  const [legacyResult, masterResult] = await Promise.all([
    client
      .from('boss_image_db')
      .select('id, boss_name, boss_key, boss_image_url, category')
      .order('boss_name', { ascending: true })
      .limit(1000),
    client
      .from('boss_master')
      .select('icon_id, monster_model_id, source_monster_model_id, canonical_name, boss_name, boss_key, boss_image_url, image_status')
      .eq('is_visible', true)
      .neq('boss_image_url', '')
      .order('boss_name', { ascending: true })
      .limit(1000),
  ]);

  if (legacyResult.error && masterResult.error) {
    throw new Error(`보스 DB를 불러오지 못했습니다: ${masterResult.error.message || legacyResult.error.message}`);
  }

  const legacyRows = legacyResult.error ? [] : legacyResult.data || [];
  const masterRows = masterResult.error ? [] : normalizeBossMasterImageRows(masterResult.data || []);
  const lookup = buildBossImageLookup([...legacyRows, ...masterRows]);

  return {
    rows: [...lookup.values()],
    lookup,
    warnings: [
      legacyResult.error ? `기존 보스 이미지 DB 조회 실패: ${legacyResult.error.message}` : '',
      masterResult.error ? `공용 보스 DB 조회 실패: ${masterResult.error.message}` : '',
    ].filter(Boolean),
  };
}

export async function loadBossImageCatalog({ forceRefresh = false } = {}) {
  if (!forceRefresh && cacheFresh(bossImageCatalogCache)) return bossImageCatalogCache.value;
  if (!forceRefresh && bossImageCatalogPromise) return bossImageCatalogPromise;

  const promise = fetchBossImageCatalog()
    .then((value) => {
      bossImageCatalogCache = { value, cachedAt: Date.now() };
      return value;
    })
    .finally(() => {
      if (bossImageCatalogPromise === promise) bossImageCatalogPromise = null;
    });
  bossImageCatalogPromise = promise;
  return promise;
}
